import React from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from './ModalHeader';

interface WarningConfirmModalProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** 'danger' paints the confirm button red for destructive actions (delete). */
  confirmVariant?: 'primary' | 'danger';
  /**
   * Opt the confirm button onto Alt+S. Off by default — see the hotkey note
   * below. Reserve it for the "Save Anyway?" gates inside a save flow, where the
   * user pressed Alt+S to get here and pressing it again means "yes, go on".
   */
  confirmHotkey?: 'S';
  /** Render inside a native dialog's top layer instead of portaling to body. */
  inPlace?: boolean;
}

// Portaled centered dialog for advisory warn-and-confirm flows. Mirrors the
// inline dispatch-warning dialog in DriverSelectorCell — same z-index, scrim,
// and button layout — but parameterized so other surfaces can reuse it without
// re-implementing the JSX. Existing inline copies can migrate later; this stays
// scoped to new callers for now.
export const WarningConfirmModal: React.FC<WarningConfirmModalProps> = ({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  confirmVariant = 'primary',
  confirmHotkey,
  inPlace = false,
}) => {
  // Alt+C cancels. Confirm gets NO chord by default: callers use this for
  // advisory warn-and-continue gates (discard edits, dispatch anyway), and those
  // are the last actions that should be one keystroke away. The layer still
  // swallows Alt+S so it can't reach the Save on the surface underneath. A
  // caller whose confirm IS the save continuing (confirmHotkey="S") gets Alt+S
  // routed to onConfirm instead of swallowed.
  useAltHotkeys(
    { C: onCancel, S: confirmHotkey === 'S' ? onConfirm : undefined },
    undefined,
    { layer: open },
  );

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-scrim/50" onClick={onCancel} />
      <div
        ref={panelRef}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative bg-cmp-modal rounded-lg shadow-xl w-full max-w-sm mx-4"
      >
        <ModalHeader title={title} onClose={onCancel} dragHandleProps={dragHandleProps} />
        <div className="p-6">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {message}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-label bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                confirmVariant === 'danger'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
  return inPlace ? modal : createPortal(modal, document.body);
};

export default WarningConfirmModal;
